import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Input,
  Button,
  IconButton,
  Switch,
  Badge,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiShield,
  FiUser,
  FiArrowLeft,
  FiUsers,
  FiMoreHorizontal,
  FiChevronUp,
  FiChevronDown,
  FiCopy,
  FiFilter,
  FiCheck,
  FiLink,
  FiAlertCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

// ─── Palette "Linear / Vercel" ───
const c = {
  bg: "#FAFAFA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  borderSubtle: "#F3F4F6",
  hover: "#F9FAFB",
  text: "#111827",
  textSec: "#6B7280",
  textMuted: "#9CA3AF",
  blue: "#2563EB",
  blueHover: "#1D4ED8",
  blueLight: "#EFF6FF",
  green: "#10B981",
  greenLight: "#ECFDF5",
  red: "#EF4444",
  redHover: "#DC2626",
  redLight: "#FEF2F2",
  amber: "#F59E0B",
  amberLight: "#FFFBEB",
  purple: "#8B5CF6",
  purpleLight: "#F5F3FF",
};

const FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif";

// ─── Sous-composants ───

const StatPill = ({ label, value, color, icon: Icon }) => (
  <HStack
    spacing={2.5}
    px={3}
    py={2}
    bg="white"
    border="1px solid"
    borderColor={c.border}
    borderRadius="8px"
    minW="130px"
  >
    <Flex
      w="28px"
      h="28px"
      borderRadius="6px"
      bg={color.bg}
      color={color.text}
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Icon size={14} />
    </Flex>
    <Box>
      <Text fontSize="11px" color={c.textSec} fontWeight="500" lineHeight="1">
        {label}
      </Text>
      <Text fontSize="18px" fontWeight="600" color={c.text} lineHeight="1.2" mt={0.5}>
        {value}
      </Text>
    </Box>
  </HStack>
);

const UserFormFields = ({ user, setUser, showLabels = true }) => (
  <VStack spacing={4} align="stretch">
    <FormControl>
      {showLabels && (
        <FormLabel fontSize="13px" fontWeight="500" color={c.text} mb={1.5}>
          Nom d'utilisateur
        </FormLabel>
      )}
      <Input
        value={user.name}
        onChange={(e) => setUser({ ...user, name: e.target.value })}
        placeholder="ex. jean.dupont"
        fontSize="14px"
        borderColor={c.border}
        borderRadius="6px"
        height="38px"
        _hover={{ borderColor: c.textMuted }}
        _focus={{
          borderColor: c.blue,
          boxShadow: `0 0 0 3px ${c.blueLight}`,
        }}
      />
    </FormControl>
    <FormControl>
      {showLabels && (
        <FormLabel fontSize="13px" fontWeight="500" color={c.text} mb={1.5}>
          Mot de passe
        </FormLabel>
      )}
      <Input
        value={user.password}
        onChange={(e) => setUser({ ...user, password: e.target.value })}
        placeholder="Mot de passe"
        fontSize="14px"
        borderColor={c.border}
        borderRadius="6px"
        height="38px"
        _hover={{ borderColor: c.textMuted }}
        _focus={{
          borderColor: c.blue,
          boxShadow: `0 0 0 3px ${c.blueLight}`,
        }}
      />
    </FormControl>
    <FormControl>
      {showLabels && (
        <FormLabel fontSize="13px" fontWeight="500" color={c.text} mb={1.5}>
          <HStack spacing={2}>
            <Box color={c.teal}>
              <FiLink size={13} />
            </Box>
            <Text>ID commercial iColl</Text>
          </HStack>
        </FormLabel>
      )}
      <Input
        value={user.id_icoll ?? ""}
        onChange={(e) => {
          const val = e.target.value;
          // Accepter uniquement les chiffres, mais garder la valeur en string pour l'input contrôlé
          if (val === "" || /^\d+$/.test(val)) {
            setUser({ ...user, id_icoll: val });
          }
        }}
        placeholder="ex. 42 (laisser vide si non applicable)"
        fontSize="14px"
        borderColor={c.border}
        borderRadius="6px"
        height="38px"
        inputMode="numeric"
        _hover={{ borderColor: c.textMuted }}
        _focus={{
          borderColor: c.blue,
          boxShadow: `0 0 0 3px ${c.blueLight}`,
        }}
      />
      <Text fontSize="11px" color={c.textMuted} mt={1.5}>
        Identifiant numérique du commercial dans le CRM iColl. Nécessaire pour
        que les fiches clients créées par cet utilisateur remontent
        automatiquement dans iColl à la sauvegarde.
      </Text>
    </FormControl>
    <FormControl
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={3}
      bg={c.hover}
      borderRadius="6px"
      border="1px solid"
      borderColor={c.borderSubtle}
    >
      <HStack spacing={2}>
        <Box color={c.amber}>
          <FiShield size={16} />
        </Box>
        <Box>
          <Text fontSize="13px" fontWeight="500" color={c.text}>
            Rôle administrateur
          </Text>
          <Text fontSize="12px" color={c.textSec}>
            Accès complet à la gestion
          </Text>
        </Box>
      </HStack>
      <Switch
        isChecked={user.admin}
        onChange={(e) => setUser({ ...user, admin: e.target.checked })}
        colorScheme="blue"
        size="md"
      />
    </FormControl>
  </VStack>
);

// ─── Composant principal ───

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [showPasswordFor, setShowPasswordFor] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", password: "", admin: false, id_icoll: "" });

  const addModal = useDisclosure();
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null); // { ids: [], label: "" }

  const navigate = useNavigate();
  const toast = useToast();

  const showToast = (title, status = "success", description) =>
    toast({
      title,
      description,
      status,
      duration: 2500,
      isClosable: true,
      position: "bottom-right",
      variant: "subtle",
    });

  const fetchUsers = () => {
    const token = localStorage.getItem("token");
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUsers(res.data))
      .catch((err) => {
        console.error("Erreur chargement users", err);
        showToast("Erreur de chargement", "error");
      });
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtering + sorting
  const processedUsers = useMemo(() => {
    let list = users;

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((u) => (u.name || "").toLowerCase().includes(q));
    }

    // Role filter
    if (roleFilter === "admin") list = list.filter((u) => u.admin);
    if (roleFilter === "standard") list = list.filter((u) => !u.admin);

    // Sort
    list = [...list].sort((a, b) => {
      let va = a[sortKey] ?? "";
      let vb = b[sortKey] ?? "";
      if (typeof va === "boolean") {
        va = va ? 1 : 0;
        vb = vb ? 1 : 0;
      } else {
        va = String(va).toLowerCase();
        vb = String(vb).toLowerCase();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [users, search, roleFilter, sortKey, sortDir]);

  const adminCount = users.filter((u) => u.admin).length;
  const standardCount = users.length - adminCount;
  const icollConfiguredCount = users.filter(
    (u) => u.id_icoll !== undefined && u.id_icoll !== null && u.id_icoll !== ""
  ).length;
  const icollMissingCount = users.length - icollConfiguredCount;

  // ─── Actions ───

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === processedUsers.length && processedUsers.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(processedUsers.map((u) => u._id)));
    }
  };

  const openEditModal = (user) => {
    setEditingUser({ ...user });
    editModal.onOpen();
  };

  const openAddModal = () => {
    setNewUser({ name: "", password: "", admin: false, id_icoll: "" });
    addModal.onOpen();
  };

  const saveNewUser = () => {
    if (!newUser.name || !newUser.password) {
      showToast("Champs requis", "warning", "Nom et mot de passe obligatoires");
      return;
    }
    axios
      .post(`${process.env.REACT_APP_SERVER_URL}/users`, newUser)
      .then(() => {
        fetchUsers();
        addModal.onClose();
        showToast("Utilisateur créé", "success", `${newUser.name} a été ajouté`);
      })
      .catch((err) => {
        console.error("Erreur création", err);
        showToast("Erreur de création", "error");
      });
  };

  const saveEdit = () => {
    if (!editingUser?.name || !editingUser?.password) {
      showToast("Champs requis", "warning", "Nom et mot de passe obligatoires");
      return;
    }
    axios
      .put(
        `${process.env.REACT_APP_SERVER_URL}/users/${editingUser._id}`,
        editingUser
      )
      .then(() => {
        fetchUsers();
        editModal.onClose();
        setEditingUser(null);
        showToast("Modifications enregistrées");
      })
      .catch((err) => {
        console.error("Erreur modification", err);
        showToast("Erreur de mise à jour", "error");
      });
  };

  const requestDelete = (ids, label) => {
    setDeleteTarget({ ids, label });
    deleteModal.onOpen();
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.ids?.length) return;
    try {
      await Promise.all(
        deleteTarget.ids.map((id) =>
          axios.delete(`${process.env.REACT_APP_SERVER_URL}/users/${id}`)
        )
      );
      fetchUsers();
      setSelected(new Set());
      deleteModal.onClose();
      setDeleteTarget(null);
      showToast(
        deleteTarget.ids.length > 1
          ? `${deleteTarget.ids.length} utilisateurs supprimés`
          : "Utilisateur supprimé",
        "info"
      );
    } catch (err) {
      console.error("Erreur suppression", err);
      showToast("Erreur de suppression", "error");
    }
  };

  const togglePasswordVisibility = (id) => {
    setShowPasswordFor((p) => ({ ...p, [id]: !p[id] }));
  };

  const copyPassword = (password) => {
    navigator.clipboard?.writeText(password);
    showToast("Copié dans le presse-papier");
  };

  // Avatar helpers
  const getInitials = (name) => (name || "??").slice(0, 2).toUpperCase();
  const getAvatarColor = (name) => {
    const palette = [
      { bg: c.blueLight, text: c.blue },
      { bg: c.greenLight, text: c.green },
      { bg: c.amberLight, text: c.amber },
      { bg: c.purpleLight, text: c.purple },
      { bg: c.redLight, text: c.red },
    ];
    const hash = (name || "").split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
    return palette[hash % palette.length];
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <Box w="10px" />;
    return sortDir === "asc" ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />;
  };

  const selectedCount = selected.size;
  const allSelected = selectedCount === processedUsers.length && processedUsers.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < processedUsers.length;

  return (
    <Box minH="100vh" bg={c.bg} fontFamily={FONT} color={c.text}>
      {/* ─── Top navigation bar ─── */}
      <Flex
        as="header"
        h="56px"
        px={6}
        bg="white"
        borderBottom="1px solid"
        borderColor={c.border}
        align="center"
        justify="space-between"
      >
        <HStack spacing={3}>
          <IconButton
            icon={<FiArrowLeft size={16} />}
            variant="ghost"
            size="sm"
            color={c.textSec}
            onClick={() => navigate("/")}
            aria-label="Retour"
            _hover={{ bg: c.hover, color: c.text }}
          />
          <Text fontSize="14px" color={c.textSec} fontWeight="500">
            Accueil
          </Text>
          <Text color={c.textMuted}>/</Text>
          <Text fontSize="14px" color={c.text} fontWeight="600">
            Utilisateurs
          </Text>
        </HStack>
        <HStack spacing={2}>
          <Text fontSize="12px" color={c.textMuted}>
            {users.length} {users.length > 1 ? "membres" : "membre"}
          </Text>
        </HStack>
      </Flex>

      {/* ─── Main content ─── */}
      <Box maxW="1400px" mx="auto" px={6} py={6}>
        {/* Page header */}
        <Flex align="flex-end" justify="space-between" mb={5} flexWrap="wrap" gap={4}>
          <Box>
            <Text fontSize="24px" fontWeight="700" color={c.text} letterSpacing="-0.4px">
              Gestion des utilisateurs
            </Text>
            <Text fontSize="13px" color={c.textSec} mt={1}>
              Gérez les accès de votre équipe au simulateur Home-Solution
            </Text>
          </Box>
          <HStack spacing={2.5} flexWrap="wrap">
            <StatPill
              label="Total"
              value={users.length}
              color={{ bg: c.blueLight, text: c.blue }}
              icon={FiUsers}
            />
            <StatPill
              label="Admins"
              value={adminCount}
              color={{ bg: c.amberLight, text: c.amber }}
              icon={FiShield}
            />
            <StatPill
              label="iColl liés"
              value={`${icollConfiguredCount}/${users.length}`}
              color={
                icollMissingCount === 0
                  ? { bg: "#ECFDF5", text: "#14B8A6" }
                  : { bg: c.amberLight, text: c.amber }
              }
              icon={FiLink}
            />
          </HStack>
        </Flex>

        {/* Toolbar */}
        <Flex
          bg="white"
          border="1px solid"
          borderColor={c.border}
          borderRadius="8px"
          p={2.5}
          mb={0}
          align="center"
          gap={2}
          borderBottomRadius={0}
          borderBottom="none"
        >
          {/* Search */}
          <Flex
            flex={1}
            maxW="360px"
            bg={c.hover}
            borderRadius="6px"
            px={3}
            align="center"
            height="34px"
            border="1px solid"
            borderColor="transparent"
            _focusWithin={{ borderColor: c.blue, bg: "white" }}
            transition="all 0.15s"
          >
            <Box color={c.textMuted} mr={2} display="flex" alignItems="center">
              <FiSearch size={14} />
            </Box>
            <Input
              placeholder="Rechercher par nom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="unstyled"
              fontSize="13px"
              color={c.text}
              _placeholder={{ color: c.textMuted }}
            />
          </Flex>

          {/* Role filter */}
          <Menu>
            <MenuButton
              as={Button}
              variant="outline"
              size="sm"
              height="34px"
              borderColor={c.border}
              borderRadius="6px"
              fontSize="13px"
              fontWeight="500"
              color={c.text}
              leftIcon={<FiFilter size={13} />}
              rightIcon={<FiChevronDown size={13} />}
              _hover={{ bg: c.hover, borderColor: c.textMuted }}
              _active={{ bg: c.hover }}
            >
              {roleFilter === "all" ? "Tous les rôles" : roleFilter === "admin" ? "Admins" : "Standards"}
            </MenuButton>
            <MenuList fontSize="13px" minW="160px">
              <MenuItem onClick={() => setRoleFilter("all")}>
                {roleFilter === "all" && <FiCheck size={12} style={{ marginRight: 8 }} />}
                Tous les rôles
              </MenuItem>
              <MenuItem onClick={() => setRoleFilter("admin")}>
                {roleFilter === "admin" && <FiCheck size={12} style={{ marginRight: 8 }} />}
                Admins uniquement
              </MenuItem>
              <MenuItem onClick={() => setRoleFilter("standard")}>
                {roleFilter === "standard" && <FiCheck size={12} style={{ marginRight: 8 }} />}
                Standards uniquement
              </MenuItem>
            </MenuList>
          </Menu>

          <Box flex={1} />

          {/* Bulk actions */}
          {selectedCount > 0 && (
            <HStack
              spacing={2}
              bg={c.blueLight}
              border="1px solid"
              borderColor={c.blue}
              borderRadius="6px"
              px={3}
              height="34px"
            >
              <Text fontSize="12px" color={c.blue} fontWeight="600">
                {selectedCount} sélectionné{selectedCount > 1 ? "s" : ""}
              </Text>
              <Divider orientation="vertical" h="16px" borderColor={c.blue} opacity={0.3} />
              <Button
                size="xs"
                variant="ghost"
                color={c.red}
                leftIcon={<FiTrash2 size={12} />}
                fontSize="12px"
                fontWeight="600"
                _hover={{ bg: c.redLight }}
                onClick={() => {
                  const ids = Array.from(selected);
                  const label =
                    ids.length === 1
                      ? users.find((u) => u._id === ids[0])?.name || ""
                      : `${ids.length} utilisateurs`;
                  requestDelete(ids, label);
                }}
              >
                Supprimer
              </Button>
            </HStack>
          )}

          {/* Add button */}
          <Button
            size="sm"
            bg={c.text}
            color="white"
            borderRadius="6px"
            fontSize="13px"
            fontWeight="600"
            height="34px"
            px={3}
            leftIcon={<FiPlus size={14} />}
            _hover={{ bg: "#000" }}
            _active={{ transform: "scale(0.98)" }}
            onClick={openAddModal}
          >
            Nouvel utilisateur
          </Button>
        </Flex>

        {/* ─── Data table ─── */}
        <Box
          bg="white"
          border="1px solid"
          borderColor={c.border}
          borderTopRadius={0}
          borderBottomRadius="8px"
          overflow="hidden"
        >
          {/* Table header */}
          <Flex
            bg={c.hover}
            borderBottom="1px solid"
            borderColor={c.border}
            h="40px"
            align="center"
            px={4}
            fontSize="11px"
            fontWeight="600"
            color={c.textSec}
            textTransform="uppercase"
            letterSpacing="0.4px"
          >
            <Box w="36px">
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={someSelected}
                onChange={toggleSelectAll}
                colorScheme="blue"
                size="sm"
              />
            </Box>
            <Flex
              flex="1 1 200px"
              align="center"
              cursor="pointer"
              onClick={() => toggleSort("name")}
              _hover={{ color: c.text }}
              gap={1}
            >
              Utilisateur
              <SortIcon column="name" />
            </Flex>
            <Box flex="1 1 180px">Mot de passe</Box>
            <Flex
              w="120px"
              align="center"
              cursor="pointer"
              onClick={() => toggleSort("admin")}
              _hover={{ color: c.text }}
              gap={1}
            >
              Rôle
              <SortIcon column="admin" />
            </Flex>
            <Box w="180px">ID iColl</Box>
            <Box w="80px" textAlign="right">
              Actions
            </Box>
          </Flex>

          {/* Table rows */}
          {processedUsers.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={16}
              color={c.textMuted}
            >
              <Box mb={3}>
                <FiUsers size={32} />
              </Box>
              <Text fontSize="14px" fontWeight="500" color={c.textSec}>
                {search || roleFilter !== "all"
                  ? "Aucun utilisateur ne correspond aux filtres"
                  : "Aucun utilisateur"}
              </Text>
              {!search && roleFilter === "all" && (
                <Button
                  mt={4}
                  size="sm"
                  variant="outline"
                  leftIcon={<FiPlus size={13} />}
                  onClick={openAddModal}
                  borderColor={c.border}
                  color={c.text}
                  fontSize="13px"
                >
                  Créer le premier utilisateur
                </Button>
              )}
            </Flex>
          ) : (
            processedUsers.map((user, idx) => {
              const isSelected = selected.has(user._id);
              const isPwVisible = showPasswordFor[user._id];
              const avatar = getAvatarColor(user.name);
              const isLast = idx === processedUsers.length - 1;
              const maskedPw = "•".repeat(
                Math.max(6, Math.min(user.password?.length || 8, 14))
              );

              return (
                <Flex
                  key={user._id}
                  h="52px"
                  align="center"
                  px={4}
                  borderBottom={isLast ? "none" : "1px solid"}
                  borderColor={c.borderSubtle}
                  bg={isSelected ? c.blueLight : "white"}
                  _hover={{ bg: isSelected ? c.blueLight : c.hover }}
                  transition="background 0.1s"
                  role="group"
                >
                  {/* Checkbox */}
                  <Box w="36px">
                    <Checkbox
                      isChecked={isSelected}
                      onChange={() => toggleSelect(user._id)}
                      colorScheme="blue"
                      size="sm"
                    />
                  </Box>

                  {/* User (avatar + name) */}
                  <Flex flex="1 1 200px" align="center" minW={0}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="6px"
                      bg={avatar.bg}
                      color={avatar.text}
                      align="center"
                      justify="center"
                      fontSize="12px"
                      fontWeight="600"
                      mr={3}
                      flexShrink={0}
                    >
                      {getInitials(user.name)}
                    </Flex>
                    <Box minW={0}>
                      <Text
                        fontSize="13px"
                        fontWeight="600"
                        color={c.text}
                        isTruncated
                        lineHeight="1.3"
                      >
                        {user.name || "(sans nom)"}
                      </Text>
                      <Text fontSize="11px" color={c.textMuted} lineHeight="1.3">
                        Compte {user.admin ? "administrateur" : "standard"}
                      </Text>
                    </Box>
                  </Flex>

                  {/* Password */}
                  <Flex flex="1 1 180px" align="center" minW={0}>
                    <Text
                      fontSize="12px"
                      color={c.textSec}
                      fontFamily="'SF Mono', Menlo, Monaco, Consolas, monospace"
                      mr={2}
                      isTruncated
                    >
                      {isPwVisible ? user.password : maskedPw}
                    </Text>
                    <HStack
                      spacing={0}
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition="opacity 0.15s"
                    >
                      <IconButton
                        icon={isPwVisible ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                        size="xs"
                        variant="ghost"
                        color={c.textSec}
                        onClick={() => togglePasswordVisibility(user._id)}
                        aria-label="Toggle password"
                        _hover={{ color: c.text, bg: c.borderSubtle }}
                        minW="22px"
                        h="22px"
                      />
                      <IconButton
                        icon={<FiCopy size={12} />}
                        size="xs"
                        variant="ghost"
                        color={c.textSec}
                        onClick={() => copyPassword(user.password)}
                        aria-label="Copier"
                        _hover={{ color: c.text, bg: c.borderSubtle }}
                        minW="22px"
                        h="22px"
                      />
                    </HStack>
                  </Flex>

                  {/* Role badge */}
                  <Box w="120px">
                    {user.admin ? (
                      <HStack
                        spacing={1.5}
                        display="inline-flex"
                        bg={c.amberLight}
                        color={c.amber}
                        px={2}
                        py={1}
                        borderRadius="4px"
                        border="1px solid"
                        borderColor="#FDE68A"
                      >
                        <FiShield size={11} />
                        <Text fontSize="11px" fontWeight="600">
                          Admin
                        </Text>
                      </HStack>
                    ) : (
                      <HStack
                        spacing={1.5}
                        display="inline-flex"
                        bg={c.hover}
                        color={c.textSec}
                        px={2}
                        py={1}
                        borderRadius="4px"
                        border="1px solid"
                        borderColor={c.border}
                      >
                        <FiUser size={11} />
                        <Text fontSize="11px" fontWeight="600">
                          Standard
                        </Text>
                      </HStack>
                    )}
                  </Box>

                  {/* ID iColl */}
                  <Box w="180px">
                    {user.id_icoll !== undefined && user.id_icoll !== null && user.id_icoll !== "" ? (
                      <HStack
                        spacing={1.5}
                        display="inline-flex"
                        bg={c.tealLight}
                        color={c.teal}
                        px={2}
                        py={1}
                        borderRadius="4px"
                        border="1px solid #99F6E4"
                      >
                        <FiLink size={11} />
                        <Text
                          fontSize="11px"
                          fontWeight="600"
                          fontFamily="'SF Mono', Menlo, Monaco, monospace"
                        >
                          #{user.id_icoll}
                        </Text>
                      </HStack>
                    ) : (
                      <HStack
                        spacing={1.5}
                        display="inline-flex"
                        bg={c.amberLight}
                        color={c.amber}
                        px={2}
                        py={1}
                        borderRadius="4px"
                        border="1px solid #FDE68A"
                      >
                        <FiAlertCircle size={11} />
                        <Text fontSize="11px" fontWeight="600">
                          Non configuré
                        </Text>
                      </HStack>
                    )}
                  </Box>

                  {/* Actions */}
                  <HStack
                    w="80px"
                    justify="flex-end"
                    spacing={0.5}
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.15s"
                  >
                    <IconButton
                      icon={<FiEdit2 size={13} />}
                      size="xs"
                      variant="ghost"
                      color={c.textSec}
                      onClick={() => openEditModal(user)}
                      aria-label="Modifier"
                      _hover={{ color: c.blue, bg: c.blueLight }}
                    />
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreHorizontal size={14} />}
                        size="xs"
                        variant="ghost"
                        color={c.textSec}
                        aria-label="Plus d'actions"
                        _hover={{ color: c.text, bg: c.borderSubtle }}
                      />
                      <MenuList fontSize="13px" minW="180px">
                        <MenuItem
                          icon={<FiEdit2 size={13} />}
                          onClick={() => openEditModal(user)}
                        >
                          Modifier
                        </MenuItem>
                        <MenuItem
                          icon={<FiCopy size={13} />}
                          onClick={() => {
                            navigator.clipboard?.writeText(user._id);
                            showToast("ID copié");
                          }}
                        >
                          Copier l'ID
                        </MenuItem>
                        <MenuItem
                          icon={<FiCopy size={13} />}
                          onClick={() => copyPassword(user.password)}
                        >
                          Copier le mot de passe
                        </MenuItem>
                        <Divider />
                        <MenuItem
                          icon={<FiTrash2 size={13} />}
                          color={c.red}
                          onClick={() => requestDelete([user._id], user.name)}
                        >
                          Supprimer
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Flex>
              );
            })
          )}
        </Box>

        {/* Footer info */}
        <Flex
          justify="space-between"
          align="center"
          mt={3}
          fontSize="12px"
          color={c.textMuted}
        >
          <Text>
            {processedUsers.length} résultat{processedUsers.length > 1 ? "s" : ""}
            {search && ` pour "${search}"`}
          </Text>
          <Text>Home-Solution · Cluster home-se-prod</Text>
        </Flex>
      </Box>

      {/* ─── Add User Modal ─── */}
      <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} size="md" isCentered>
        <ModalOverlay bg="rgba(17, 24, 39, 0.5)" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="10px" border="1px solid" borderColor={c.border}>
          <ModalHeader fontSize="16px" fontWeight="600" color={c.text} pb={2}>
            Nouvel utilisateur
            <Text fontSize="13px" fontWeight="400" color={c.textSec} mt={0.5}>
              Ajoutez un membre à votre équipe
            </Text>
          </ModalHeader>
          <ModalCloseButton color={c.textSec} />
          <ModalBody>
            <UserFormFields user={newUser} setUser={setNewUser} />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="outline"
              size="sm"
              borderColor={c.border}
              color={c.text}
              onClick={addModal.onClose}
              fontSize="13px"
              fontWeight="500"
              _hover={{ bg: c.hover }}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              bg={c.text}
              color="white"
              onClick={saveNewUser}
              fontSize="13px"
              fontWeight="600"
              _hover={{ bg: "#000" }}
              leftIcon={<FiPlus size={13} />}
            >
              Créer l'utilisateur
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ─── Edit User Modal ─── */}
      <Modal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.onClose();
          setEditingUser(null);
        }}
        size="md"
        isCentered
      >
        <ModalOverlay bg="rgba(17, 24, 39, 0.5)" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="10px" border="1px solid" borderColor={c.border}>
          <ModalHeader fontSize="16px" fontWeight="600" color={c.text} pb={2}>
            Modifier l'utilisateur
            <Text fontSize="13px" fontWeight="400" color={c.textSec} mt={0.5}>
              {editingUser?.name}
            </Text>
          </ModalHeader>
          <ModalCloseButton color={c.textSec} />
          <ModalBody>
            {editingUser && (
              <UserFormFields user={editingUser} setUser={setEditingUser} />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="outline"
              size="sm"
              borderColor={c.border}
              color={c.text}
              onClick={() => {
                editModal.onClose();
                setEditingUser(null);
              }}
              fontSize="13px"
              fontWeight="500"
              _hover={{ bg: c.hover }}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              bg={c.blue}
              color="white"
              onClick={saveEdit}
              fontSize="13px"
              fontWeight="600"
              _hover={{ bg: c.blueHover }}
              leftIcon={<FiCheck size={13} />}
            >
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ─── Delete Confirmation Modal ─── */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => {
          deleteModal.onClose();
          setDeleteTarget(null);
        }}
        size="md"
        isCentered
      >
        <ModalOverlay bg="rgba(17, 24, 39, 0.5)" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="10px" border="1px solid" borderColor={c.border}>
          <ModalHeader fontSize="16px" fontWeight="600" color={c.text} pb={2}>
            <HStack spacing={2}>
              <Flex
                w="32px"
                h="32px"
                borderRadius="8px"
                bg={c.redLight}
                color={c.red}
                align="center"
                justify="center"
              >
                <FiTrash2 size={16} />
              </Flex>
              <Text>Confirmer la suppression</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={c.textSec} />
          <ModalBody>
            <Text fontSize="14px" color={c.text} mb={2}>
              Supprimer{" "}
              <Text as="span" fontWeight="600">
                {deleteTarget?.label}
              </Text>{" "}
              ?
            </Text>
            <Text fontSize="13px" color={c.textSec}>
              Cette action est irréversible. L'utilisateur perdra immédiatement
              l'accès au simulateur Home-Solution.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="outline"
              size="sm"
              borderColor={c.border}
              color={c.text}
              onClick={() => {
                deleteModal.onClose();
                setDeleteTarget(null);
              }}
              fontSize="13px"
              fontWeight="500"
              _hover={{ bg: c.hover }}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              bg={c.red}
              color="white"
              onClick={confirmDelete}
              fontSize="13px"
              fontWeight="600"
              _hover={{ bg: c.redHover }}
              leftIcon={<FiTrash2 size={13} />}
            >
              Supprimer définitivement
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Admin;
